import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { memo } from "react";
import { OutboundLabelForm } from "../outbound-labels";

export const pointsSettingsForm = groupInput({
  enabled: input<boolean>(),
  uspsLabelPointsAwarded: input<number>(),
  fedexLabelPointsAwarded: input<number>(),
  upsLabelPointsAwarded: input<number>(),
  amazonLabelPointsAwarded: input<number>(),
  firstMileLabelPointsAwarded: input<number>(),
  dhlEcommerceLabelPointsAwarded: input<number>(),
});

export type PointsSettingsForm = InputSchema.Input<typeof pointsSettingsForm>;

export type PointsSettingsValue = InputSchema.Value<typeof pointsSettingsForm>;

export const pointsSettingsDefault: PointsSettingsValue = {
  enabled: false,
  uspsLabelPointsAwarded: 5,
  fedexLabelPointsAwarded: 5,
  upsLabelPointsAwarded: 5,
  amazonLabelPointsAwarded: 5,
  firstMileLabelPointsAwarded: 5,
  dhlEcommerceLabelPointsAwarded: 5,
};

export const PointsProgramSettingsCard = memo(
  function PointsProgramSettingsCard({ input }: { input: OutboundLabelForm }) {
    const { pointsSettings } = input.inputs;
    const enabled = pointsSettings.inputs.enabled.value;

    return (
      <Card title="Redo Points Program" w="full">
        <Flex dir="column" gap="md">
          <Text fontSize="sm" textColor="warning">
            Warning: This feature will credit earned points to the merchant's
            balance at the end of the month. Only enable if you are sure you
            want to do this.
          </Text>
          <Text fontSize="sm" textColor="error">
            Do not enable without talking to an engineer first.
          </Text>
          <RedoToggle
            label="Enable Redo Points Program"
            setValue={pointsSettings.inputs.enabled.setValue}
            size="sm"
            value={pointsSettings.inputs.enabled.value}
          />

          <Text fontSize="sm" textColor="secondary">
            Configure points awarded per label for each carrier. One point is
            equal to one cent when redeemed.
          </Text>

          <RedoIncrementDecrement
            disabled={!enabled}
            label="USPS points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.uspsLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.uspsLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="FedEx points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.fedexLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.fedexLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="UPS points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.upsLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.upsLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="Amazon points per label"
            max={100}
            min={0}
            setValue={pointsSettings.inputs.amazonLabelPointsAwarded.setValue}
            step={1}
            value={pointsSettings.inputs.amazonLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="FirstMile points per label"
            max={100}
            min={0}
            setValue={
              pointsSettings.inputs.firstMileLabelPointsAwarded.setValue
            }
            step={1}
            value={pointsSettings.inputs.firstMileLabelPointsAwarded.value}
          />

          <RedoIncrementDecrement
            disabled={!enabled}
            label="DHL eCommerce points per label"
            max={250}
            min={0}
            setValue={
              pointsSettings.inputs.dhlEcommerceLabelPointsAwarded.setValue
            }
            step={1}
            value={pointsSettings.inputs.dhlEcommerceLabelPointsAwarded.value}
          />
        </Flex>
      </Card>
    );
  },
);
